import type { EmployeePresenceState } from './Employee'

export interface CustomerRequest {
	name: string
	phoneNumber?: string
	faxNumber?: string
	parentReseller?: string
	emailAddress?: string
	website?: string
	notes?: string
	addreses: CustomerAddress[]
	finance: {
		vatNumber: string
		accountingSystemId?: string
		invoiceMethod: CustomerFinanceInvoiceMethod
		emailAddress?: string
		electronicAddress?: string
		paymentTerms: CustomerFinancePaymentTerm
		sipUsageLimit: number
		mvnoUsageLimit: number
		channelLimit: number
		numberRentProduct?: string
		durationMethod?: 0 | 1
		skipInvoicing: boolean
	}
	serviceLevelAgreements?: {
		remote?: {
			responseTime: CustomerServiceLevelAgreementResponseTime
			days: CustomerServiceLevelAgreementDay
			period: CustomerServiceLevelAgreementPeriod
		},
		onsite?: {
			responseTime: CustomerServiceLevelAgreementResponseTime
			days: CustomerServiceLevelAgreementDay
			period: CustomerServiceLevelAgreementPeriod
		},
		services?: {
			phoneSupportErrors: boolean
			phoneSupportChanges: boolean
			onsiteInclusive: boolean
			hardwareInclusive: boolean
			mitelSwas: boolean
			prescriptionStatus: boolean
		}
	}
	isReseller:	boolean
	resellerPortal?: string
	resellerInvoice?: boolean
	resellerNextInvoiceNumber?: number
	resellerSkin?: CustomerSkin
	resellerEmailAsSender?: boolean
	resellerCustomerGroups: { _id: string, name: string }[]
	features: CustomerFeature[]
	authenticationSecurity:	CustomerAuthenticationSecurity
	custom:	unknown
	storage: {
		total: number
	},
	mvno: {
		dataTopUpSms: boolean
	},
	integrations: CustomerIntegrations
	bindingDate?: Date
	simTag?: string
	customerGroup?: string
}

export interface CustomerResponse {
	_id: string
	name: string
	phoneNumber?: string
	faxNumber?: string
	state: CustomerState
	parentReseller?: string
	emailAddress?: string
	website?: string
	notes?: string
	addresses: CustomerAddress[]
	callEventSubscribers: CustomerCallEventSubscriber[]
	finance: {
		vatNumber: string
		accountingSystemId?: string
		invoiceMethod: CustomerFinanceInvoiceMethod
		emailAddress?: string
		electronicAddress?: string
		paymentTerms: CustomerFinancePaymentTerm
		sipUsageLimit: number
		mvnoUsageLimit: number
		channelLimit: number
		numberRentProduct?: string
		numberRentLastProduct?: string
		numberRentUntil?: Date
		numberRentCount: {
			singles: number
			hundreds: number
		},
		durationMethod?: 0 | 1
		billingCustomers: CustomerBillingCustomer[]
		skipInvoicing: boolean
	},
	/** These are used by PowerNet, and minimum resellerAdmins-level needed to configure them - only gettable by resellerAdmins and above */
	serviceLevelAgreements?: {
		remote?: {
			responseTime: CustomerServiceLevelAgreementResponseTime
			days: CustomerServiceLevelAgreementDay
			period: CustomerServiceLevelAgreementPeriod
		},
		onsite?: {
			responseTime: CustomerServiceLevelAgreementResponseTime
			days: CustomerServiceLevelAgreementDay
			period: CustomerServiceLevelAgreementPeriod
		},
		services?: {
			phoneSupportErrors: boolean
			phoneSupportChanges: boolean
			onsiteInclusive: boolean
			hardwareInclusive: boolean
			mitelSwas: boolean
			prescriptionStatus: boolean
		}
	}
	isReseller:	boolean
	resellerPortal?: string
	resellerInvoice?: boolean
	resellerNextInvoiceNumber?: number
	resellerSkin?: CustomerSkin
	resellerEmailAsSender?: boolean
	resellerCustomerGroups: { _id: string, name: string }[]
	features: CustomerFeature[]
	authenticationSecurity:	CustomerAuthenticationSecurity
	custom:	unknown
	storage: {
		used: number
		total: number
	},
	mvno: {
		dataTopUpSms: boolean
	},
	integrations: CustomerIntegrations
	bindingDate?: Date
	simTag?: string
	customerGroup?: string
	mvnoSimStats?: {
		autoAssignTdc: boolean
		autoAssignTelenor: boolean
	}
}

export interface CustomerAddress {
	_id: string
	primary: boolean
	alternativeName?: string
	address: string
	zip: string
	city: string
	state?: string
	country?: string
	fixedNumber?: string
	faxNumber?: string
	municipalityCode?: number
}

export interface CustomerCallEventSubscriber {
	_id: string
	url: string
	method:	CustomerCallEventSubscriberMethod
	selection: CustomerCallEventSubscriberSelection
}

export interface CustomerIntegrations {
	billing?: {
		economic?: {
			enabled: boolean
			agreementGrantToken?: string
			accrual?: boolean
		},
		rackbeat?: {
			enabled: boolean
			bearerToken?: string
		}
	},
	licenses?: {
		also?: {
			enabled?: boolean
			username?: string
			password?: string
			customerId?: string
		}
	},
	ms365?: {
		enabled: boolean
		tenantId?: string
		verified?: boolean
		calendarSync?: boolean
		contactsSync?: boolean
		orgContactsSync?: boolean
		presenceSync?: boolean
		calendarShowAsMappings?: {
			free: CustomerMS365IntegrationCalendarShowAsMappingState
			tentative: CustomerMS365IntegrationCalendarShowAsMappingState
			busy: CustomerMS365IntegrationCalendarShowAsMappingState
			oof: CustomerMS365IntegrationCalendarShowAsMappingState
			workingElsewhere: CustomerMS365IntegrationCalendarShowAsMappingState
		},
		calendarCategoryMappings?: {
			category: string
			presence: EmployeePresenceState
		}
	},
	mvnoPrivate?: {
		enabled: boolean
		portal?: string
	}
}

export interface CustomerBillingCustomer {
	_id: string
	name: string
	vatNumber: string
}

/** Customer features */
export enum CustomerFeature {
	/** Basics, addresses, financial, usage limits, employees */
	CUSTOMER = 'CUSTOMER',
	/** SIP trunks */
	SIP = 'SIP',
	/** Mobile phones */
	MVNO = 'MVNO',
	/** Hosted PBX, SIP phones, music on hold, Webhooks (auto-enables SIP, MVNO, CUSTOMER) */
	PBX = 'PBX',
	/** Telecom Call Enhancer */
	TCE = 'TCE',
	/** Domain management */
	DNS = 'DNS',
	/** Fiber & xDSL */
	INTERNET = 'INTERNET',
	/** TV */
	TV = 'TV',
	/** Private IPTV customer */
	IPTVPRIVATE = 'IPTVPRIVATE',
	/** Business IPTV customer */
	IPTVBUSINESS = 'IPTVBUSINESS',
	/** FlexCare HealthTech */
	FLEXCARE = 'FLEXCARE',
	/** ZeroTier networking */
	ZEROTIER = 'ZEROTIER',
	/** External licenses */
	EXTERNAL_LICENSES = 'EXTERNAL_LICENSES'
}

/** Skin for styling Communicator apps */
export enum CustomerSkin {
	/** Default Telecom X skin */
	EARTH = 'EARTH',
	/** PowerNet skin */
	TEAL = 'TEAL',
	/** Dark mode skin */
	DARK = 'DARK'
}

/** Customer state */
export enum CustomerState {
	/** Enabled and active */
	ACTIVE = 'ACTIVE',
	/** All services blocked */
	BLOCKED = 'BLOCKED',
	/** Deleted */
	DELETED = 'DELETED'
}

export enum CustomerFinanceInvoiceMethod {
	MAIL = 'MAIL',
	EMAIL = 'EMAIL',
	ELECTRONIC = 'ELECTRONIC'
}

export enum CustomerFinancePaymentTerm {
	DAYS_5 = 'DAYS_5',
	DAYS_7 = 'DAYS_7',
	DAYS_14 = 'DAYS_14',
	DAYS_30 = 'DAYS_30',
	DAYS_60 = 'DAYS_60',
	DAYS_90 = 'DAYS_90',
	CURRENT_MONTH_PLUS_DAYS_7 = 'CURRENT_MONTH_PLUS_DAYS_7',
	CURRENT_MONTH_PLUS_DAYS_14 = 'CURRENT_MONTH_PLUS_DAYS_14',
	CURRENT_MONTH_PLUS_DAYS_30 = 'CURRENT_MONTH_PLUS_DAYS_30'
}

export enum CustomerAuthenticationSecurity {
	/** Regular username + password */
	NONE = 'NONE',
	/** Username + Password + SMS token */
	SMS = 'SMS',
	/** Username + Password + TOTP authenticator token */
	AUTHENTICATOR = 'AUTHENTICATOR'
}

export type CustomerMS365IntegrationCalendarShowAsMappingState = EmployeePresenceState | 'IGNORE'

export enum CustomerCallEventSubscriberMethod {
	POSTARGS = 'POSTARGS',
	POSTXML1 = 'POSTXML1',
	POSTXML2 = 'POSTXML2',
	POSTJSON = 'POSTJSON',
	GET1 = 'GET1',
	GET2 = 'GET2'
}

export enum CustomerCallEventSubscriberSelection {
	SIP = 'SIP',
	MVNO = 'MVNO',
	ALL = 'ALL'
}

export type CustomerServiceLevelAgreementResponseTime = 1 | 3 | 8 | 24
export type CustomerServiceLevelAgreementDay = 'WEEK_DAYS' | 'WEEK_DAYS_AND_SATURDAY' | 'ALL'
export type CustomerServiceLevelAgreementPeriod = '07:45-16:00' | '08:00-22:00' | '00:00-23:59'
